import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Phone, ArrowRight } from "lucide-react";
import { scrollToId } from "@/lib/api";

const LINKS = [
  { href: "#services", label: "Services" },
  { href: "#realisations", label: "Réalisations" },
  { href: "#avis", label: "Avis" },
  { href: "#contact", label: "Contact" },
];

export default function Header({ settings }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActive(`#${e.target.id}`)),
      { rootMargin: "-35% 0px -60% 0px" }
    );
    LINKS.forEach((l) => {
      const el = document.querySelector(l.href);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle("menu-open", open);
    if (open) {
      document.documentElement.style.overflow = "hidden";
      window.__lenis?.stop();
    } else {
      document.documentElement.style.overflow = "";
      window.__lenis?.start();
    }
  }, [open]);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    setTimeout(() => scrollToId(href), open ? 120 : 0);
  };

  const tel = (settings.phone || "").replace(/\s/g, "");

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 border-b border-line transition-[background-color] duration-200 ${
          scrolled ? "bg-cream/85 backdrop-blur-md" : "bg-cream"
        }`}
      >
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-[76px] flex items-center gap-6">
          <a href="#top" onClick={(e) => go(e, "#top")} data-testid="header-logo" aria-label="MARY SERVICES — retour en haut" className="shrink-0 flex items-center gap-2.5">
            <img src="/logo.png" alt="MARY SERVICES" className="h-7 w-auto" />
            <span className="hidden sm:block font-display font-semibold text-xl leading-none text-ink tracking-tight">MARY<span className="text-terra"> SERVICES</span></span>
          </a>

          <span className="sm:hidden absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 font-display font-semibold text-lg leading-none text-ink tracking-tight pointer-events-none" data-testid="header-brand-mobile">
            MARY<span className="text-terra"> SERVICES</span>
          </span>

          <nav className="hidden md:flex flex-1 items-center justify-center gap-8 lg:gap-10" aria-label="Navigation principale">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={(e) => go(e, l.href)}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="relative py-2 text-[15px] font-medium text-ink/75 transition-colors duration-200 hover:text-forest"
              >
                {l.label}
                <span
                  className={`absolute -bottom-0.5 left-0 h-[2px] bg-terra transition-all duration-200 ${active === l.href ? "w-full" : "w-0"}`}
                  aria-hidden="true"
                />
              </a>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-6 shrink-0">
            <a
              href={`tel:${tel}`}
              data-testid="header-phone-link"
              className="hidden lg:inline-flex items-center gap-2 text-sm font-medium text-body2 transition-colors hover:text-forest"
            >
              <Phone className="h-4 w-4" />
              {settings.phone}
            </a>
            <a
              href="#contact"
              onClick={(e) => go(e, "#contact")}
              data-testid="nav-quote-cta"
              className="cta-signature cta-signature-sm"
            >
              Demander un devis
              <span className="cta-arrow"><ArrowRight className="h-4 w-4" /></span>
            </a>
          </div>

          <button
            onClick={() => setOpen(!open)}
            aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
            aria-expanded={open}
            data-testid="mobile-menu-button"
            className="md:hidden ml-auto h-11 w-11 grid place-items-center"
          >
            <span className="relative block w-6 h-4">
              <span className={`absolute left-0 top-0 h-[2px] w-6 bg-ink transition-transform duration-200 ${open ? "translate-y-[7px] rotate-45" : ""}`} />
              <span className={`absolute left-0 bottom-0 h-[2px] w-6 bg-ink transition-transform duration-200 ${open ? "-translate-y-[7px] -rotate-45" : ""}`} />
            </span>
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            className="fixed inset-x-0 top-16 bottom-0 z-40 bg-cream md:hidden flex flex-col px-6 pt-12 pb-10"
            data-testid="mobile-menu"
          >
            <nav className="space-y-7" aria-label="Navigation mobile">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={(e) => go(e, l.href)}
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  className="block font-display font-semibold text-3xl text-ink"
                >
                  {l.label}
                </a>
              ))}
            </nav>
            <div className="mt-auto space-y-5">
              <a href={`tel:${tel}`} data-testid="mobile-menu-phone" className="flex items-center gap-3 text-lg font-medium text-body2">
                <Phone className="h-5 w-5 text-terra" />
                {settings.phone}
              </a>
              <a
                href="#contact"
                onClick={(e) => go(e, "#contact")}
                data-testid="mobile-menu-quote-cta"
                className="block w-full text-center rounded-full bg-terra text-cream font-semibold py-4 text-base"
              >
                Demander un devis
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
